import type { Decimal } from "../types/api";

// Amounts arrive as exact decimal strings; they are only converted to numbers for display.
const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const integer = new Intl.NumberFormat("en-US");
const weight = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
const dateTime = new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" });
const dateOnly = new Intl.DateTimeFormat("en-US", { dateStyle: "medium" });

export function formatMoney(value: Decimal | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return money.format(Number(value));
}

export function formatSignedMoney(value: Decimal | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const number = Number(value);
  if (number === 0) return money.format(0);
  return `${number > 0 ? "+" : "−"}${money.format(Math.abs(number))}`;
}

export function formatWeight(value: Decimal | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${weight.format(Number(value))} lb`;
}

export function formatSignedWeight(value: Decimal | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const number = Number(value);
  if (number === 0) return "0 lb";
  return `${number > 0 ? "+" : "−"}${weight.format(Math.abs(number))} lb`;
}

export function formatCount(value: number): string {
  return integer.format(value);
}

export function formatDateTime(value: string | null | undefined): string {
  return value ? dateTime.format(new Date(value)) : "—";
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  // Plain dates ("2026-09-22") are calendar dates, not instants: don't shift them by time zone.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
  return dateOnly.format(date);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function plural(count: number, one: string, many = `${one}s`): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

export function varianceTone(value: Decimal | null | undefined): "up" | "down" | "flat" {
  if (value === null || value === undefined) return "flat";
  const number = Number(value);
  return number > 0 ? "up" : number < 0 ? "down" : "flat";
}
